import {Tooltip} from '@mui/material'
import type {FC, ReactElement} from 'react'
import {StrictMode} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useExportSignalValueTrackersExcelMutation} from '@apis/base'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useSnackbar} from '@hooks/useSnackbar'
import {
  useTimeIntervalContext,
  type TimeIntervalContextProps,
} from '@shared/providers/TimeIntervalProvider'
import BasicExportExcelButton from '@app/shared/components/BasicExportExcelButton'

interface ExportSignalValueTrackersExcelProps {
  disabled?: boolean
}

const ExportSignalValueTrackersExcel: FC<ExportSignalValueTrackersExcelProps> = ({
  disabled = false,
}): ReactElement => {
  const [t] = useCustomTranslation('common')
  const [showNotification] = useSnackbar()
  const [exportExcel, {isLoading}] = useExportSignalValueTrackersExcelMutation()
  const [searchParams] = useSearchParams()
  const {startTime, endTime} = useTimeIntervalContext() as TimeIntervalContextProps

  const handleExport = async (): Promise<void> => {
    const searchTerm = searchParams.get('searchSignalValueTracker')

    const filters = {
      productionAreaId: searchParams.get('productionAreaId') ?? undefined,
      lineId: searchParams.get('lineId') ?? undefined,
      equipmentId: searchParams.get('equipmentId') ?? undefined,
    }

    try {
      const blob = await exportExcel({
        search: searchTerm ?? undefined,
        startDate: startTime.toISOString(),
        endDate: endTime.toISOString(),
        signalId: searchParams.get('signalId') ?? undefined,
        filters: filters,
      }).unwrap()

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'signal_value_trackers_export.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (_error) {
      showNotification({
        message: t('messages.unexpected_error'),
        severity: 'error',
      })
    }
  }

  return (
    <StrictMode>
      <Tooltip title={t('export_excel')}>
        <span>
          <BasicExportExcelButton
            isLoading={isLoading}
            onClick={handleExport}
            label={t('export_excel')}
            dataTestId="mbai-signal-value-trackers-export-excel"
            disabled={disabled || isLoading}
          />
        </span>
      </Tooltip>
    </StrictMode>
  )
}

export default ExportSignalValueTrackersExcel
